import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Layers } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function Sectores() {
  const [items, setItems] = useState([]);
  const [jefes, setJefes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ numero: "", nombre: "", jefe_user_id: "" });
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const { user } = useAuth();
  const readOnly = ["jefe_sector", "supervisor_zona"].includes(user?.role);

  const load = async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/sectores"),
        api.get("/usuarios", { params: { role: "jefe_sector" } }),
      ]);
      setItems(s.data);
      setJefes(u.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm({ numero: "", nombre: "", jefe_user_id: "" }); setOpen(true); };
  const startEdit = (s) => { setEditing(s); setForm({ numero: s.numero, nombre: s.nombre || "", jefe_user_id: s.jefe_user_id || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, jefe_user_id: form.jefe_user_id || null };
      if (editing) await api.put(`/sectores/${editing.id}`, payload);
      else await api.post("/sectores", payload);
      toast.success(editing ? "Sector actualizado" : "Sector creado");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/sectores/${confirm.id}`);
      toast.success("Sector eliminado"); setConfirm(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppLayout>
      <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Administración</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>Sectores</h1>
          </div>
          {!readOnly && (
            <Button data-testid="btn-add-sector" onClick={startCreate} className="bg-[#235B4E] hover:bg-[#1D4A40] text-white">
              <Plus className="w-4 h-4 mr-1" /> Nuevo sector
            </Button>
          )}
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB]">
                <TableHead>Número</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead>Jefe de sector</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10">
                  <Layers className="w-8 h-8 text-[#9CA3AF] mx-auto mb-2" />
                  <div className="text-sm text-[#545454]">Sin sectores registrados</div>
                </TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s.id} data-testid={`sector-row-${s.id}`}>
                  <TableCell className="font-mono">{s.numero}</TableCell>
                  <TableCell>{s.nombre || "—"}</TableCell>
                  <TableCell>{s.jefe?.name || s.jefe?.email || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(s)} data-testid={`btn-edit-sector-${s.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirm(s)} data-testid={`btn-del-sector-${s.id}`}><Trash2 className="w-4 h-4 text-[#9F2241]" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Editar sector" : "Nuevo sector"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-xs">Número *</Label>
              <Input data-testid="sector-numero" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value.toUpperCase() })} required />
            </div>
            <div><Label className="text-xs">Nombre</Label>
              <Input data-testid="sector-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} />
            </div>
            <div><Label className="text-xs">Jefe de sector</Label>
              <Select value={form.jefe_user_id || "__none__"} onValueChange={(v) => setForm({ ...form, jefe_user_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="sector-jefe"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Sin asignar —</SelectItem>
                  {jefes.map((j) => <SelectItem key={j.id} value={j.id}>{j.name || j.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-sector" type="submit" className="bg-[#235B4E] hover:bg-[#1D4A40]">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar sector?</AlertDialogTitle>
            <AlertDialogDescription>Se desvincularán las zonas y usuarios asociados.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#9F2241] hover:bg-[#801C34]">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
